import React from 'react';
import {Box, Tab, Tabs} from '@mui/material';
import './ViewCodingForms.scss';
import CodingFormHeader from './coding-form-header/CodingFormHeader';
import {t} from 'i18next';
import {useEffect, useState, useRef} from 'react';
import UnsavedChangesModal from '../create-edit-coding-form/unsaved-changes-modal/UnsavedChangesModal';
import CreateEditCodingForm, {
  CreateEditCodingFormHandle,
} from '../create-edit-coding-form/CreateEditCodingForm';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {ReactComponent as SearchIcon} from '../../../assets/images/icon-search.svg';
import ManageCodingForms from '../manage-coding-forms/ManageCodingForms';
import {ICodingFormDetailsForEditResponse} from '../../../api/types/document.type';

interface TabData {
  label: string;
  disabled: boolean;
  content: React.ReactNode;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`view-coding-forms-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`view-coding-forms-div-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

const ViewCodingForms: React.FC = () => {
  const [value, setValue] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [dbSearchInput, setDbSearchInput] = useState('');
  const [editFormData, setEditFormData] = useState<
    ICodingFormDetailsForEditResponse['data'] | undefined
  >();
  const [canEditTab, setCanEditTab] = useState(false);

  useEffect(() => {
    const input = setTimeout(() => {
      setDbSearchInput(searchInput);
    }, 500);
    return () => clearTimeout(input);
  }, [searchInput]);

  const formRef = useRef<CreateEditCodingFormHandle>(null);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  const [savingInModal, setSavingInModal] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const prepareNewForm = () => {
    clearFormState();
    setEditFormData(undefined);
    setCanEditTab(true);
    setValue(1);
    setPendingTabChange(null);
    setFormKey((previousKey) => previousKey + 1);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!formRef.current || typeof formRef.current.save !== 'function') {
      setHasUnsavedChanges(false);
      setEditFormData(undefined);
      setCanEditTab(false);
      return true;
    }

    try {
      setSavingInModal(true);
      await formRef.current.save();
      setHasUnsavedChanges(false);
      setEditFormData(undefined);
      setCanEditTab(false);
      return true;
    } catch (err) {
      setShowUnsavedModal(false);
      console.log(err);
      return false;
    } finally {
      setSavingInModal(false);
    }
  };

  const handleSaveAndChange = async () => {
    const ok = await handleSave();
    if (!ok) {
      return;
    }

    if (pendingTabChange === 1) {
      prepareNewForm();
    } else if (pendingTabChange !== null) {
      setValue(pendingTabChange);
      setPendingTabChange(null);
    } else {
      setPendingTabChange(null);
    }
    setShowUnsavedModal(false);
  };

  const handleDiscardAndChange = () => {
    if (formRef.current && typeof formRef.current.resetDirty === 'function') {
      formRef.current.resetDirty();
    } else if (
      formRef.current &&
      typeof formRef.current.forceReset === 'function'
    ) {
      formRef.current.forceReset();
    }

    if (pendingTabChange === 1) {
      prepareNewForm();
    } else if (pendingTabChange !== null) {
      clearFormState();
      setEditFormData(undefined);
      setValue(pendingTabChange);
      setPendingTabChange(null);
    }
    setShowUnsavedModal(false);
  };

  const clearFormState = () => {
    setHasUnsavedChanges(false);
    setEditFormData(undefined);
    if (formRef.current && typeof formRef.current.forceReset === 'function') {
      formRef.current.forceReset();
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && !canEditTab) {
      // Prevent direct access to create/edit tab
      return;
    }
    if (value === 1 && hasUnsavedChanges) {
      setPendingTabChange(newValue);
      setShowUnsavedModal(true);
      return;
    }
    clearFormState();
    setEditFormData(undefined);
    setValue(newValue);
    if (newValue !== 1) setCanEditTab(false);
  };

  const handleEditForm = (
    formData: ICodingFormDetailsForEditResponse['data']
  ) => {
    setEditFormData(formData); // Set form data for editing
    setCanEditTab(true);
    setValue(1); // Switch to edit tab
  };

  const handleNewForm = () => {
    clearFormState();
    setEditFormData(undefined);
    setCanEditTab(true);
    setPendingTabChange(null);
    setFormKey((previousKey) => previousKey + 1);
    setValue(1);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };
  const tabData: TabData[] = [
    {
      label: t('codingForms.manage.title'),
      disabled: false,
      content: (
        <ManageCodingForms
          searchText={dbSearchInput}
          onEditForm={handleEditForm}
        />
      ),
    },
    {
      label: t('codingForms.manage.createForm'),
      disabled: !canEditTab,
      content: (
        <CreateEditCodingForm
          key={formKey}
          ref={formRef}
          codingForm={editFormData}
          onClose={() => {
            setValue(0);
            setCanEditTab(false);
          }}
          onSave={() => {
            setHasUnsavedChanges(false);
            setEditFormData(undefined);
            setCanEditTab(false);
            setValue(0);
          }}
          onChangeStatus={setHasUnsavedChanges}
        />
      ),
    },
  ];

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      name: `simple-tab-${index}`,
      'data-testid': `simple-tab-${index}`,
    };
  }

  useEffect(() => {
    if (value !== 1 && canEditTab) {
      setCanEditTab(false);
    }
  }, [value]);

  return (
    <>
      <Box className="coding-forms-container">
        <CodingFormHeader
          onButtonClick={() => {
            if (hasUnsavedChanges && value === 1) {
              setPendingTabChange(1);
              setShowUnsavedModal(true);
            } else {
              handleNewForm();
            }
          }}
          isButtonDisable={false}
          currentTab={value}
        />
        <Box>
          <Box className="tab-header">
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="assign user tabs"
              className="assign-user-tabs"
            >
              {tabData.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  disabled={tab.disabled}
                  className="assign-user-tab"
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
            {value === 0 && (
              <Box className="search-box-div">
                <ArrTextField
                  fullWidth
                  sizeVariant="medium"
                  className="search-box"
                  value={searchInput}
                  onChange={handleSearchChange}
                  type="search"
                  dataTestId="coding-form-search"
                />
              </Box>
            )}
          </Box>
          <Box className="tab-content">
            {tabData.map((tab, index) => (
              <TabPanel key={tab.label} value={value} index={index}>
                {tab.content}
              </TabPanel>
            ))}
          </Box>
        </Box>
      </Box>

      <UnsavedChangesModal
        showModal={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onSave={handleSaveAndChange}
        onDiscard={handleDiscardAndChange}
        saving={savingInModal}
      />
    </>
  );
};
export default ViewCodingForms;
